import { useState, useEffect, useCallback, memo, useMemo } from 'react';
import {
  Menu,
  MenuItem,
  ListItemText,
  Radio,
  Button,
  Typography,
  Divider,
  Box
} from '@mui/material';

interface ColumnSelectionMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  nistColumns: string[];
  cisColumns: string[];
  pciDssColumns: string[];
  sparkColumns?: string[];
  onApply: (selectedNist: string, selectedCis: string, selectedPciDss: string, selectedSpark?: string) => void;
  currentSelectedNist?: string;
  currentSelectedCis?: string;
  currentSelectedPciDss?: string;
  currentSelectedSpark?: string;
}

// Memoized section component to prevent unnecessary re-renders
const ColumnSection = memo(({ 
  title, 
  columns, 
  selectedColumn, 
  onSelect 
}: { 
  title: string;
  columns: string[];
  selectedColumn: string;
  onSelect: (column: string) => void;
}) => (
  <Box sx={{ p: 1.5 }}>
    <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1 }}>{title}</Typography>
    {columns.map((column) => (
      <MenuItem 
        key={column} 
        onClick={() => onSelect(column)}
        dense
        sx={{ 
          minHeight: '32px',
          py: 0.5
        }}
      >
        <Radio 
          checked={selectedColumn === column} 
          size="small"
          sx={{ 
            p: 0.5,
            '&.Mui-checked': {
              transform: 'scale(0.9)'
            }
          }}
        />
        <ListItemText 
          primary={column} 
          primaryTypographyProps={{
            variant: 'body2',
            sx: { ml: 0.5 }
          }}
        />
      </MenuItem>
    ))}
  </Box>
));

const ColumnSelectionMenu: React.FC<ColumnSelectionMenuProps> = ({
  anchorEl,
  open,
  onClose,
  nistColumns,
  cisColumns,
  pciDssColumns,
  sparkColumns = [],
  onApply,
  currentSelectedNist,
  currentSelectedCis,
  currentSelectedPciDss,
  currentSelectedSpark
}) => {
  const [selectedNist, setSelectedNist] = useState<string>('');
  const [selectedCis, setSelectedCis] = useState<string>('');
  const [selectedPciDss, setSelectedPciDss] = useState<string>('');
  const [selectedSpark, setSelectedSpark] = useState<string>('');

  // Initialize with current selections or default to first column
  useEffect(() => {
    if (open) {
      setSelectedNist(currentSelectedNist || (nistColumns && nistColumns[0]) || '');
      setSelectedCis(currentSelectedCis || (cisColumns && cisColumns[0]) || '');
      setSelectedPciDss(currentSelectedPciDss || (pciDssColumns && pciDssColumns[0]) || '');
      setSelectedSpark(currentSelectedSpark || (sparkColumns && sparkColumns[0]) || '');
    }
  }, [open, nistColumns, cisColumns, pciDssColumns, sparkColumns, currentSelectedNist, currentSelectedCis, currentSelectedPciDss, currentSelectedSpark]);

  const handleNistSelect = useCallback((column: string) => {
    setSelectedNist(column);
  }, []);

  const handleCisSelect = useCallback((column: string) => {
    setSelectedCis(column);
  }, []);

  const handlePciDssSelect = useCallback((column: string) => {
    setSelectedPciDss(column);
  }, []);

  const handleSparkSelect = useCallback((column: string) => {
    setSelectedSpark(column);
  }, []);

  const handleApply = useCallback(() => {
    onApply(selectedNist, selectedCis, selectedPciDss, selectedSpark);
    onClose();
  }, [selectedNist, selectedCis, selectedPciDss, selectedSpark, onApply, onClose]);

  // Memoize menu props
  const menuProps = useMemo(() => ({
    style: {
      maxHeight: 400,
      width: '300px'
    }
  }), []);

  // Memoize sections to prevent re-renders
  const sections = useMemo(() => (
    <>
      {nistColumns && nistColumns.length > 0 && (
        <>
          <ColumnSection
            title="NIST Columns"
            columns={nistColumns}
            selectedColumn={selectedNist}
            onSelect={handleNistSelect}
          />
          <Divider />
        </>
      )}
      {cisColumns && cisColumns.length > 0 && (
        <>
          <ColumnSection
            title="CIS Columns"
            columns={cisColumns}
            selectedColumn={selectedCis}
            onSelect={handleCisSelect}
          />
          <Divider />
        </>
      )}
      {pciDssColumns && pciDssColumns.length > 0 && (
        <>
          <ColumnSection
            title="PCI DSS Columns"
            columns={pciDssColumns}
            selectedColumn={selectedPciDss}
            onSelect={handlePciDssSelect}
          />
          <Divider />
        </>
      )}
      {sparkColumns && sparkColumns.length > 0 && (
        <ColumnSection
          title="SPARK Columns"
          columns={sparkColumns}
          selectedColumn={selectedSpark}
          onSelect={handleSparkSelect}
        />
      )}
    </>
  ), [nistColumns, cisColumns, pciDssColumns, sparkColumns, selectedNist, selectedCis, selectedPciDss, selectedSpark, handleNistSelect, handleCisSelect, handlePciDssSelect, handleSparkSelect]);

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      PaperProps={menuProps}
      transitionDuration={0}
      disablePortal={false}
      sx={{
        '& .MuiMenu-paper': {
          boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.15)',
          zIndex: 9999,
          maxWidth: '300px',
          minWidth: '280px'
        },
        zIndex: 9999
      }}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
    >
      {sections}
      <Divider />
      <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'center' }}>
        <Button 
          variant="contained" 
          onClick={handleApply}
          // Only groups that have columns need a selection (a switched-off framework has none)
          disabled={
            (nistColumns.length > 0 && !selectedNist) ||
            (cisColumns.length > 0 && !selectedCis) ||
            (pciDssColumns.length > 0 && !selectedPciDss)
          }
          size="small"
          sx={{ minWidth: '80px' }}
        >
          Apply
        </Button>
      </Box>
    </Menu>
  );
};

// Use React.memo with a custom comparison function
export default memo(ColumnSelectionMenu, (prevProps, nextProps) => {
  return prevProps.open === nextProps.open &&
         prevProps.anchorEl === nextProps.anchorEl &&
         prevProps.nistColumns === nextProps.nistColumns &&
         prevProps.cisColumns === nextProps.cisColumns &&
         prevProps.pciDssColumns === nextProps.pciDssColumns &&
         prevProps.sparkColumns === nextProps.sparkColumns;
}); 